import { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { theme, Icon, Text, Button, Calendar, SpeechBubble, styles as baseStyles } from '@socrait/react';
import { useQueryClient } from '@tanstack/react-query';

import { trackEvent } from '~/gtag';
import { ErrorBox } from '~/components/ErrorBox';
import { Loading } from '~/components/Loading';
import {
  useViewStudentDataScreensFeature,
  useViewWeeklyInsightsFeature,
  useCalendarPickerOnReviewPageFeature,
} from '~/featureFlags';
import {
  useReviewScorecards,
  useReviewActionItems,
  useInsights,
  useMoments,
  useReviewDayCountsQuery,
  REVIEW_DAY_COUNTS_QUERY_KEY,
} from '~/services/review';
import { Period, SessionStatus } from '~/services/types';
import { getFormattedDate } from '~/utils/date';
import { preferenceStore } from '~/store/preferenceStore';

import { ReviewActionItems } from './ReviewActionItems';
import { ReviewScoreCards } from './ReviewScoreCards';
import { Reminders, sortReminders } from './ui/Reminders';

const ROSTER_BUTTON_SIZE = 40;

const isToday = (date: Date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export type ReviewPeriodProps = {
  activePeriod: Period;
  date: Date;
  onSessionStatusChange: (status?: SessionStatus) => void;
  onRosterPress: () => void;
  setDate: (date: Date) => void;
};

export function ReviewPeriod({ activePeriod, date, onSessionStatusChange, onRosterPress, setDate }: ReviewPeriodProps) {
  const isViewWeeklyInsightsEnabled = useViewWeeklyInsightsFeature();
  const isViewStudentDataScreensEnabled = useViewStudentDataScreensFeature();
  const isCalendarPickerEnabled = useCalendarPickerOnReviewPageFeature();

  const hasSeenRosterWhatsNew = preferenceStore((s) => s.hasSeenRosterWhatsNew);
  const setHasSeenRosterWhatsNew = preferenceStore((s) => s.setHasSeenRosterWhatsNew);

  const [viewedYear, setViewedYear] = useState(date.getFullYear());
  const [viewedMonth, setViewedMonth] = useState(date.getMonth() + 1);

  const {
    data: scorecardData,
    error: scorecardError,
    status: scorecardStatus,
  } = useReviewScorecards({ date, periodId: activePeriod.id });
  const {
    data: actionItemsData,
    status: actionItemsStatus,
    error: actionItemsError,
  } = useReviewActionItems({
    date,
    periodId: activePeriod.id,
  });
  const { data: insightsData, status: insightsStatus } = useInsights({
    date,
    periodId: activePeriod.id,
  });
  const { data: momentsData, status: momentsStatus } = useMoments({
    date,
    periodId: activePeriod.id,
  });
  const { isPending: dayCountsIsPending, isFetching: dayCountsIsFetching } = useReviewDayCountsQuery({
    periodId: activePeriod.id,
    year: viewedYear,
    month: viewedMonth,
  });

  // The Calendar resets its internal viewed-month to `activeDate`'s month on reopen, so
  // `viewedYear`/`viewedMonth` can be stale vs. what it displays. Merge every month cached
  // under this period so whichever month it shows has data. The `useReviewDayCountsQuery`
  // subscription above is what drives re-renders when new month data arrives.
  const queryClient = useQueryClient();
  const dayCounts = queryClient
    .getQueriesData<Record<string, number>>({ queryKey: [REVIEW_DAY_COUNTS_QUERY_KEY, activePeriod.id] })
    .reduce<Record<string, number>>((acc, [, counts]) => (counts ? { ...acc, ...counts } : acc), {});

  useEffect(() => {
    if (scorecardData?.session?.status || actionItemsData?.session?.status) {
      onSessionStatusChange((scorecardData?.session?.status || actionItemsData?.session?.status)!);
    }
  }, [scorecardData?.session?.status, actionItemsData?.session?.status, onSessionStatusChange]);

  const hasPraiseOrWarningItems = Boolean(
    scorecardData?.scorecards?.praises?.length || scorecardData?.scorecards?.warnings?.length,
  );

  const handlePrevDay = useCallback(() => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() - 1);
    trackEvent('day_prev');
    setDate(currentDate);
  }, [date, setDate]);

  const handleNextDay = useCallback(() => {
    const currentDate = new Date(date);
    currentDate.setDate(currentDate.getDate() + 1);
    trackEvent('day_next');
    setDate(currentDate);
  }, [date, setDate]);

  const handleChangeMonth = useCallback((year: number, month: number) => {
    setViewedYear(year);
    setViewedMonth(month);
  }, []);

  const handleSelectDate = useCallback(
    (selectedDate: Date) => {
      trackEvent('day_select');
      setDate(selectedDate);
    },
    [setDate],
  );

  const remindersData = sortReminders(actionItemsData?.actionItems?.filter((item) => item.type === 'reminders') || []);
  const displayedReminders = isViewWeeklyInsightsEnabled
    ? remindersData
    : remindersData.filter((item) => !item.insightId);

  return (
    <View style={styles.outerContainer}>
      <View style={styles.leftContainer}>
        <View style={styles.classInfoRow}>
          <Text style={styles.classInfo}>{activePeriod?.name || 'Class Summary'}</Text>
          {isViewStudentDataScreensEnabled && (
            <View style={styles.rosterButtonWrapper}>
              <Button
                icon="group"
                onPress={onRosterPress}
                containerStyle={styles.rosterButton}
                telemetryLabel="roster-button"
              />
              {!hasSeenRosterWhatsNew && (
                <View style={styles.whatsNewBubble}>
                  <SpeechBubble
                    arrowPlacement="top-left"
                    arrowOffset={ROSTER_BUTTON_SIZE / 2}
                    onDismiss={() => setHasSeenRosterWhatsNew(true)}
                    bubbleStyle={styles.bubble}>
                    <View>
                      <Text style={styles.bubbleHeading}>What's New</Text>
                      <Text style={styles.bubbleBody}>
                        You can now view your class roster, track individual student trends, and generate messages about
                        behavior.
                      </Text>
                      <Button
                        name="Got it"
                        onPress={() => setHasSeenRosterWhatsNew(true)}
                        containerStyle={styles.gotItButton}
                        fontStyle={styles.gotItButtonText}
                      />
                    </View>
                  </SpeechBubble>
                </View>
              )}
            </View>
          )}
        </View>
        {isCalendarPickerEnabled ? (
          <Calendar
            activeDate={date}
            dayCounts={dayCounts}
            isLoading={dayCountsIsPending && dayCountsIsFetching}
            onSelectDate={handleSelectDate}
            onChangeMonth={handleChangeMonth}
          />
        ) : (
          <View style={styles.dateContainer}>
            <Pressable onPress={handlePrevDay}>
              <Icon type="leftArrow" />
            </Pressable>
            <Text style={styles.date}>{getFormattedDate(date)}</Text>
            <Pressable onPress={handleNextDay} disabled={isToday(date)}>
              <Icon type="rightArrow" fill={isToday(date) ? theme.colors.gray : theme.colors.navy} />
            </Pressable>
          </View>
        )}
        {scorecardStatus === 'pending' ? (
          <Loading backgroundColor={theme.colors.transparent} />
        ) : scorecardError ? (
          <ErrorBox />
        ) : (
          <ReviewScoreCards scorecardData={scorecardData} />
        )}
      </View>
      <View style={styles.rightContainer}>
        <ReviewActionItems
          date={date}
          hasPraiseOrWarningItems={hasPraiseOrWarningItems}
          actionItemsData={actionItemsData}
          actionItemsQueryStatus={actionItemsStatus}
          actionItemsError={actionItemsError}
          period={activePeriod}
          insightsData={insightsData}
          insightsQueryStatus={insightsStatus}
          momentsData={momentsData}
          momentsQueryStatus={momentsStatus}
        />
        <Reminders remindersData={displayedReminders} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'row',
    backgroundColor: theme.colors.lightGray,
    position: 'relative',
  },
  leftContainer: {
    width: 370,
    height: '100%',
    maxHeight: '100%',
    backgroundColor: theme.colors.white,
    gap: theme.spacing.xlarge,
    paddingHorizontal: theme.spacing.xlarge,
    paddingVertical: theme.spacing.xlarge + theme.spacing.medium,
    overflowY: 'visible',
    zIndex: 10,
  },
  rightContainer: {
    height: '100%',
    flex: 1,
    paddingHorizontal: theme.spacing.xxlarge,
    paddingVertical: theme.spacing.xxlarge,
    backgroundColor: theme.colors.lightGray,
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xxlarge,
    overflowY: 'auto',
  },
  classInfoRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 10,
  },
  classInfo: {
    ...baseStyles.title,
    margin: 0,
    flex: 1,
  },
  rosterButtonWrapper: {
    position: 'relative',
    flexShrink: 0,
    zIndex: 10,
  },
  rosterButton: {
    width: ROSTER_BUTTON_SIZE,
    height: ROSTER_BUTTON_SIZE,
  },
  whatsNewBubble: {
    position: 'absolute',
    top: ROSTER_BUTTON_SIZE,
    left: 0,
    width: 275,
  },
  bubble: {
    paddingVertical: theme.spacing.large,
  },
  bubbleHeading: {
    color: theme.colors.white,
    fontWeight: theme.fontWeights.bold,
    marginBottom: theme.spacing.small,
  },
  bubbleBody: {
    color: theme.colors.white,
    fontSize: theme.fontSizes.small,
    marginBottom: theme.spacing.medium,
  },
  gotItButton: {
    alignSelf: 'flex-start',
    height: 30,
    paddingHorizontal: theme.spacing.medium,
    marginTop: theme.spacing.medium,
  },
  gotItButtonText: {
    fontSize: theme.fontSizes.xsmall,
  },
  dateContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.xlarge,
    marginVertical: theme.spacing.xlarge,
  },
  date: {
    fontSize: theme.fontSizes.medium,
    fontFamily: theme.fonts.medium,
    fontWeight: theme.fontWeights.medium,
  },
});
